import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/react';

import { configureAccountDataAutoSync } from './accountDataAutoSync';
import {
  AccountAuthContext,
  AccountAuthReadyContext,
  AccountIdentityContext,
  type GetAccountToken,
} from './accountAuthToken';

const E2E_ACCOUNT_TOKEN_STORAGE_KEY = 'bunko.e2e.accountToken';
const E2E_ACCOUNT_TOKEN_READY_STORAGE_KEY = 'bunko.e2e.accountTokenReady';
const E2E_ACCOUNT_TOKEN_READY_EVENT = 'bunko:e2e-account-token-ready';

function e2eAccountToken(): string | undefined {
  if ((import.meta.env?.MODE as string | undefined) !== 'test') return undefined;
  return window.localStorage.getItem(E2E_ACCOUNT_TOKEN_STORAGE_KEY) ?? undefined;
}

function e2eAccountAuthReady(): boolean {
  if ((import.meta.env?.MODE as string | undefined) !== 'test') return true;
  if (!window.localStorage.getItem(E2E_ACCOUNT_TOKEN_STORAGE_KEY)) return true;
  return window.localStorage.getItem(E2E_ACCOUNT_TOKEN_READY_STORAGE_KEY) !== 'false';
}

export function AccountAuthProvider({ children }: { children: ReactNode }) {
  const [accountAuthReady, setAccountAuthReady] = useState(() => e2eAccountAuthReady());
  const [, setAccountTokenVersion] = useState(0);
  const accountIdentity = e2eAccountToken();
  const getAccountToken: GetAccountToken = useCallback(
    async () => (accountAuthReady ? e2eAccountToken() : undefined),
    [accountAuthReady],
  );

  useEffect(() => {
    if ((import.meta.env?.MODE as string | undefined) !== 'test') return;

    const updateAccountToken = () => {
      setAccountAuthReady(e2eAccountAuthReady());
      setAccountTokenVersion((version) => version + 1);
    };
    window.addEventListener(E2E_ACCOUNT_TOKEN_READY_EVENT, updateAccountToken);
    return () => window.removeEventListener(E2E_ACCOUNT_TOKEN_READY_EVENT, updateAccountToken);
  }, []);

  useEffect(() => {
    configureAccountDataAutoSync(getAccountToken);
    return () => configureAccountDataAutoSync(undefined);
  }, [getAccountToken]);

  return (
    <AccountAuthReadyContext.Provider value={accountAuthReady}>
      <AccountIdentityContext.Provider value={accountIdentity}>
        <AccountAuthContext.Provider value={getAccountToken}>
          {children}
        </AccountAuthContext.Provider>
      </AccountIdentityContext.Provider>
    </AccountAuthReadyContext.Provider>
  );
}

export function ClerkAccountAuthProvider({ children }: { children: ReactNode }) {
  const { getToken, isLoaded, isSignedIn, sessionId, userId } = useAuth();
  const getAccountToken: GetAccountToken = useCallback(async () => {
    if (!isLoaded || !isSignedIn || !sessionId || !userId) return undefined;
    return (await getToken()) ?? undefined;
  }, [getToken, isLoaded, isSignedIn, sessionId, userId]);
  useEffect(() => {
    configureAccountDataAutoSync(getAccountToken);
    return () => configureAccountDataAutoSync(undefined);
  }, [getAccountToken]);

  return (
    <AccountAuthReadyContext.Provider value={isLoaded}>
      <AccountIdentityContext.Provider value={userId ?? undefined}>
        <AccountAuthContext.Provider value={getAccountToken}>
          {children}
        </AccountAuthContext.Provider>
      </AccountIdentityContext.Provider>
    </AccountAuthReadyContext.Provider>
  );
}
